import { useState } from "react";
import AgentIcon from "../../../assets/svg/agent-icon";
import StarIcon from "../../../assets/svg/star-icon";
import { FeedbackForm } from "./FeedbackForm";
import { ContactUsForm } from "./ContactUsForm";
import { MoreForYouCardData, MoreForYouItemData } from "../utils/types";

export const MoreForYouSection = ({
  title = "More for you",
  description = "If you’re happy with our services, please share your feedback or contact us anytime with any questions.",
  moreForYouItems = [],
}: {
  title?: string;
  description?: string;
  moreForYouItems?: MoreForYouItemData[];
}) => {
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isContactUsModalOpen, setIsContactUsModalOpen] = useState(false);
  const getMoreForYouCtaText = (code: MoreForYouItemData["code"]) => {
    switch (code) {
      case "contact_us":
        return "Reach out to us";
      case "send_feedback":
        return "Share feedback";
      default:
        return "View";
    }
  };
  const toCardData = (item?: MoreForYouItemData): MoreForYouCardData | undefined => {
    if (!item) {
      return undefined;
    }
    return {
      title: item.title || "-",
      description: item.description || "-",
      ctaText: getMoreForYouCtaText(item.code),
    };
  };
  const contactUsItem = toCardData(
    moreForYouItems.find((item) => item.code === "contact_us")
  );
  const sendFeedbackItem = toCardData(
    moreForYouItems.find((item) => item.code === "send_feedback")
  );
  const hasContactUsItem = Boolean(contactUsItem);
  const hasSendFeedbackItem = Boolean(sendFeedbackItem);

  const handleShareFeedback = () => {
    setIsFeedbackModalOpen(true);
  };

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
  };

  const handleOpenContactUsModal = () => {
    setIsContactUsModalOpen(true);
  };

  const handleCloseContactUsModal = () => {
    setIsContactUsModalOpen(false);
  };

  return (
    <div className="mb-12">
      <div className="flex flex-col gap-1">
        <span className="text-base font-medium text-heading-text dark:text-heading-text-dk">
          {title}
        </span>
        <span className="text-xs text-data dark:text-data-dk">
          {description}
        </span>
      </div>
      <div className="flex gap-4 pr-4 mt-3">
        <div className="group w-[370px] h-[120px] flex flex-col justify-between p-4 border border-border-gray dark:border-border-gray-dk rounded-lg hover:border-selected-option-highlight dark:hover:border-selected-option-highlight-dk hover:bg-selected-option-highlight dark:hover:bg-selected-option-highlight-dk transition-all duration-300">
          <AgentIcon className="w-6 h-6 text-verba-indigo dark:text-verba-indigo-dk group-hover:text-verba-blue dark:group-hover:text-verba-blue-dk" />
          <div className="flex items-end justify-between">
            <div className="flex flex-col gap-1">
              <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk">
                {contactUsItem?.title || "-"}
              </span>
              <span className="text-xs text-data dark:text-data-dk">
                {contactUsItem?.description || "-"}
              </span>
            </div>
            {hasContactUsItem && (
              <button
                type="button"
                className="hidden group-hover:block w-[140px] h-max px-3 py-[3px] rounded-md text-md font-semibold text-pure-white dark:text-pure-white-dk bg-verba-indigo dark:bg-verba-indigo-dk border border-border-gray dark:border-border-gray-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:text-fixed-white dark:hover:text-fixed-white-dk transition-all duration-300"
                onClick={handleOpenContactUsModal}
              >
                {contactUsItem?.ctaText || "Reach out to us"}
              </button>
            )}
          </div>
        </div>

        <div className="group w-[570px] h-[120px] flex flex-col justify-between p-4 border border-border-gray dark:border-border-gray-dk rounded-lg hover:border-card-yellow-stroke dark:hover:border-card-yellow-stroke-dk hover:bg-card-yellow-bg dark:hover:bg-card-yellow-bg-dk transition-all duration-300">
          <StarIcon className="group-hover:hidden w-6 h-6 text-verba-indigo dark:text-verba-indigo-dk" />
          <div className="group-hover:flex hidden items-center">
            {[10, 7, 5, 4, 3].map((opacity, index) => (
              <div key={index} style={{ opacity: opacity / 10 }}>
                <StarIcon className="w-6 h-6 text-rating-yellow dark:text-rating-yellow-dk" />
              </div>
            ))}
          </div>
          <div className="flex items-end justify-between">
            <div className="flex flex-col gap-1">
              <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk">
                {sendFeedbackItem?.title || "-"}
              </span>
              <span className="text-xs text-data dark:text-data-dk">
                {sendFeedbackItem?.description || "-"}
              </span>
            </div>
            {hasSendFeedbackItem && (
              <button
                className="hidden group-hover:block w-[140px] h-max px-3 py-[3px] rounded-md text-md font-semibold text-pure-white dark:text-pure-white-dk bg-verba-indigo dark:bg-verba-indigo-dk border border-border-gray dark:border-border-gray-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:text-fixed-white dark:hover:text-fixed-white-dk transition-all duration-300"
                onClick={handleShareFeedback}
              >
                {sendFeedbackItem?.ctaText || "Share Feedback"}
              </button>
            )}
          </div>
        </div>
      </div>
      <FeedbackForm
        open={isFeedbackModalOpen}
        onClose={handleCloseFeedbackModal}
      />
      <ContactUsForm
        open={isContactUsModalOpen}
        onClose={handleCloseContactUsModal}
      />
    </div>
  );
};

export const SupportSection = MoreForYouSection;
